import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtraScheduleEntryCreateModal from './ExtraScheduleEntryCreateModal';
import ExtraScheduleEntryModal from './ExtraScheduleEntryModal';
import SingleFilterChip from '@/components/SingleFilterChip';

/**
 * Расписание доп. занятий. Недельная сетка: слева — слоты времени,
 * сверху — 7 дней (понедельник → воскресенье). В ячейке плашки занятий.
 */
const URL_SCHEMA = {
  // ISO-дата понедельника текущей недели; пусто = текущая
  week:       '',
  teacher_id: '',
  subject_id: '',
  room_id:    '',
  open:       '',  // id открытой деталки
  create:     '',  // 'YYYY-MM-DD' с дефолтной датой при открытии мастера
};

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/* ---------- helpers ---------- */
function pad(n) { return String(n).padStart(2, '0'); }
function isoOf(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}
function getMonday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const wd = x.getDay() || 7; // 1..7
  x.setDate(x.getDate() - (wd - 1));
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function shortTime(t) { return (t || '').slice(0, 5); }
function weekTitle(monday) {
  const sunday = addDays(monday, 6);
  const f = (d) => d.getDate() + ' ' + d.toLocaleDateString('ru-RU', { month: 'short' });
  const sameMonth = monday.getMonth() === sunday.getMonth();
  return sameMonth
    ? `${monday.getDate()}–${sunday.getDate()} ${monday.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`
    : `${f(monday)} – ${f(sunday)} ${sunday.getFullYear()}`;
}

const STATUS_CLS = {
  planned:   'es-entry es-entry--planned',
  conducted: 'es-entry es-entry--conducted',
  canceled:  'es-entry es-entry--canceled',
};

export default function ExtraSchedulePage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite = can('extra.schedule', 'write');

  const [params, setParams] = useUrlState(URL_SCHEMA);

  const monday = useMemo(() => {
    return params.week ? getMonday(parseIso(params.week)) : getMonday(new Date());
  }, [params.week]);
  const sunday = useMemo(() => addDays(monday, 6), [monday]);

  // Справочники для фильтров и для отображения
  const [times, setTimes]       = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms]       = useState([]);

  const [rows, setRows]    = useState([]);
  const [loading, setLoad] = useState(false);
  const [error, setError]  = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    api.get('/api/extra-lesson-times').then((r) => setTimes(r.rows || [])).catch(() => {});
    api.get('/api/extra-lessons/teachers').then((r) => setTeachers(r.rows || [])).catch(() => {});
    api.get('/api/extra-subjects').then((r) => setSubjects(r.rows || [])).catch(() => {});
    api.get('/api/rooms').then((r) => setRooms(r.rows || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoad(true); setError(null);
    const qs = new URLSearchParams();
    qs.set('from', isoOf(monday));
    qs.set('to',   isoOf(sunday));
    if (params.teacher_id) qs.set('teacher_id', params.teacher_id);
    if (params.subject_id) qs.set('subject_id', params.subject_id);
    if (params.room_id)    qs.set('room_id',    params.room_id);
    api.get('/api/extra-schedule?' + qs.toString())
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [monday.getTime(), params.teacher_id, params.subject_id, params.room_id, reloadTick]);

  // Группировка занятий по (timeId, date)
  const byCell = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = r.timeId + '|' + r.date;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return map;
  }, [rows]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);
  const todayIso = isoOf(new Date());

  const visibleTimes = useMemo(() => (times || []).filter((t) => !t.archive), [times]);

  const goToWeek = (delta) => {
    setParams({ week: isoOf(addDays(monday, delta * 7)) });
  };
  const goToToday = () => setParams({ week: '' });

  const canDelete = can('extra.schedule', 'delete');
  const onDeleteEntry = async (e, entry) => {
    e.stopPropagation();
    const subj = entry.lesson?.subject?.name || 'занятие';
    const dateStr = entry.date;
    if (!confirm(`Удалить «${subj}» от ${dateStr}? Действие необратимо.`)) return;
    try {
      await api.delete('/api/extra-schedule/' + entry.id);
      reload();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'Не удалось удалить');
    }
  };

  const hasFilters = params.teacher_id || params.subject_id || params.room_id;

  return (
    <>
      <Topbar
        breadcrumb={['Доп. занятия', 'Расписание']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row">
          <div className="es-weeknav">
            <button className="btn btn--sm" onClick={() => goToWeek(-1)} title="Предыдущая неделя">‹</button>
            <button className="btn btn--sm" onClick={goToToday}>Сегодня</button>
            <button className="btn btn--sm" onClick={() => goToWeek(1)} title="Следующая неделя">›</button>
            <span className="es-weeknav__title">{weekTitle(monday)}</span>
          </div>

          <SingleFilterChip
            label="Учитель"
            value={params.teacher_id || ''}
            options={teachers.map((t) => ({ v: String(t.id), label: t.name }))}
            onChange={(v) => setParams({ teacher_id: v })}
            placeholder="любой"
            nullLabel="Любой учитель"
          />
          <SingleFilterChip
            label="Предмет"
            value={params.subject_id || ''}
            options={subjects.filter((s) => !s.archive).map((s) => ({ v: String(s.id), label: s.name }))}
            onChange={(v) => setParams({ subject_id: v })}
            placeholder="любой"
            nullLabel="Любой предмет"
          />
          <SingleFilterChip
            label="Кабинет"
            value={params.room_id || ''}
            options={rooms.map((r) => ({ v: String(r.id), label: r.name }))}
            onChange={(v) => setParams({ room_id: v })}
            placeholder="любой"
            nullLabel="Любой кабинет"
          />

          {hasFilters && (
            <button className="btn btn--ghost btn--sm" onClick={() => setParams({ teacher_id: '', subject_id: '', room_id: '' })}>
              Сбросить
            </button>
          )}

          <div className="filterbar__spacer" />
          <span className="filterbar__meta">Занятий: <b>{rows.length}</b></span>
          <div className="filterbar__rightActions">
            <button
              className="btn btn--primary btn--sm"
              onClick={() => setParams({ create: todayIso })}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Нет прав на запись'}
            >
              <Icon name="plus" size={13} /> Новое занятие
            </button>
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}

        <div className="es-grid">
          <div className="es-grid__head">
            <div className="es-grid__corner">{loading ? '…' : ''}</div>
            {days.map((d, i) => {
              const iso = isoOf(d);
              return (
                <div
                  key={iso}
                  className={'es-grid__day' + (iso === todayIso ? ' is-today' : '')}
                >
                  <span className="es-grid__day-name">{DAY_LABELS[i]}</span>
                  <span className="es-grid__day-date">{d.getDate()}.{pad(d.getMonth() + 1)}</span>
                </div>
              );
            })}
          </div>

          {visibleTimes.length === 0 && (
            <div className="ref-empty" style={{ padding: 32 }}>
              Не заведены временные интервалы. Перейди в раздел «Временные интервалы» и добавь хотя бы один.
            </div>
          )}

          {visibleTimes.map((t) => (
            <div key={t.id} className="es-grid__row">
              <div className="es-grid__time mono">
                <span>{shortTime(t.timeStart)}</span>
                <span className="es-grid__time-sep">—</span>
                <span>{shortTime(t.timeEnd)}</span>
              </div>
              {days.map((d) => {
                const iso = isoOf(d);
                const items = byCell.get(t.id + '|' + iso) || [];
                return (
                  <div
                    key={iso}
                    className={'es-cell' + (iso === todayIso ? ' is-today' : '')}
                    onDoubleClick={() => canWrite && setParams({ create: iso })}
                    title={canWrite ? 'Двойной клик — добавить занятие в этот слот' : undefined}
                  >
                    {items.map((e) => {
                      const statusIcon = e.status === 'conducted' ? 'check'
                                       : e.status === 'canceled'  ? 'x'
                                       : 'clock';  // planned
                      const statusTitle = e.status === 'conducted' ? 'Проведено'
                                        : e.status === 'canceled'  ? 'Отменено'
                                        : 'Запланировано (не проведено)';
                      return (
                        <button
                          key={e.id}
                          type="button"
                          className={STATUS_CLS[e.status] || 'es-entry'}
                          onClick={() => setParams({ open: String(e.id) })}
                        >
                          <div className="es-entry__head">
                            <span className="es-entry__title">{e.lesson?.subject?.name || '—'}</span>
                            <span className="es-entry__status" title={statusTitle}>
                              <Icon name={statusIcon} size={11} />
                            </span>
                          </div>
                          {/* Быстрое удаление: только для не-conducted, видно при hover */}
                          {canDelete && e.status !== 'conducted' && (
                            <span
                              className="es-entry__delete"
                              role="button"
                              title="Удалить занятие"
                              onClick={(ev) => onDeleteEntry(ev, e)}
                            >
                              <Icon name="x" size={11} />
                            </span>
                          )}
                          <div className="es-entry__meta">
                            {e.lesson?.teacher?.name?.split(' ').slice(0, 2).join(' ') || '—'}
                            {e.roomName ? ' · ' + e.roomName : ''}
                          </div>
                          <div className="es-entry__bottom">
                            <span className={'es-entry__type ' + (e.lesson?.type === 'group' ? 'is-group' : 'is-individual')}>
                              {e.lesson?.type === 'group' ? 'Группа' : 'Индив.'}
                            </span>
                            {e.studentsCount !== null && (
                              <span className="es-entry__count">
                                <Icon name="users" size={10} /> {e.studentsCount}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    {canWrite && items.length === 0 && (
                      <button
                        type="button"
                        className="es-cell__add"
                        onClick={() => setParams({ create: iso })}
                        title="Добавить занятие"
                      >
                        <Icon name="plus" size={11} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {params.open && (
        <ExtraScheduleEntryModal
          entryId={Number(params.open)}
          onClose={() => { setParams({ open: '' }); reload(); }}
        />
      )}
      {params.create && (
        <ExtraScheduleEntryCreateModal
          defaultDate={params.create}
          times={visibleTimes}
          rooms={rooms}
          onClose={() => setParams({ create: '' })}
          onCreated={(id) => { setParams({ create: '', open: String(id) }); reload(); }}
        />
      )}
    </>
  );
}
